import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Calendar, DollarSign, Zap, Users, Info, ArrowLeft, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";

export default function CleanerAvailableJobs() {
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [available, setAvailable] = useState([]);
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const p = await api.get("/cleaner/profile"); setProfile(p.data);
      if (p.data?.verification_status === "approved") {
        const av = await api.get("/jobs/available"); setAvailable(av.data);
        try { const el = await api.get("/cleaner/eligibility"); setEligibility(el.data); } catch (_) { /* optional */ }
      }
    } catch (e) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const accept = async (id) => {
    try {
      await api.post(`/jobs/${id}/accept`);
      toast.success("Job accepted — dispatched to you!");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not accept"); }
  };

  if (loading || !profile) return <div className="max-w-3xl mx-auto p-12 text-slate-500" data-testid="avail-loading">Loading…</div>;

  if (profile.verification_status !== "approved") {
    return (
      <div className="max-w-3xl mx-auto p-12 text-center" data-testid="avail-not-approved">
        <ShieldAlert className="w-14 h-14 text-amber-500 mx-auto" />
        <h2 className="font-display font-bold text-2xl mt-4">Awaiting verification</h2>
        <p className="text-slate-600 mt-2">You'll see available jobs here once your identity is approved.</p>
        <Button className="mt-6 bg-teal-600 text-white" onClick={() => nav("/cleaner")}>Back to portal</Button>
      </div>
    );
  }

  const blocked = eligibility && !eligibility.eligible_for_transport_jobs;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6">
        <button onClick={() => nav("/cleaner")} className="text-xs font-mono uppercase tracking-widest text-teal-600 hover:text-teal-700 inline-flex items-center gap-1" data-testid="back-to-portal">
          <ArrowLeft className="w-3 h-3" />Back to Portal
        </button>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-slate-900 mt-2">Available Jobs</h1>
        <p className="text-sm text-slate-500 mt-1">Live pool of jobs in your zones. Refreshes every 15s.</p>
      </div>

      {blocked && (
        <Card className="mb-6 border-amber-300 bg-amber-50" data-testid="avail-risk-banner">
          <CardContent className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-amber-600 mt-0.5" />
              <div>
                <div className="font-semibold text-amber-900">Finish CLEANBAZ safety onboarding to accept jobs</div>
                <div className="text-sm text-amber-800 mt-0.5">Some requirements are missing on your safety checklist.</div>
              </div>
            </div>
            <Button onClick={() => nav("/cleaner/safety-checklist")} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="avail-goto-checklist">
              Open Safety Checklist
            </Button>
          </CardContent>
        </Card>
      )}

      {available.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-slate-500" data-testid="avail-empty">
          <Zap className="w-8 h-8 mx-auto mb-2 text-slate-400" />No jobs in your zones right now. Check back soon.
        </CardContent></Card>
      ) : available.map(j => {
        const size = j.team_size || 1;
        const seats_taken = (j.team_cleaner_ids?.length || 0);
        const per_person = j.cleaner_payout / size;
        return (
          <Card key={j.id} className="mb-4 border-teal-200 hover:shadow-lg transition-all" data-testid={`avail-card-${j.id}`}>
            <CardContent className="p-6">
              <div className="grid md:grid-cols-5 gap-4 items-center">
                <div className="md:col-span-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge className="bg-teal-100 text-teal-800 border border-teal-200 font-mono text-[10px] uppercase">Ready to dispatch</Badge>
                    {size > 1 && (
                      <Badge className="bg-blue-100 text-blue-800 border border-blue-200 font-mono text-[10px] uppercase" data-testid={`team-badge-${j.id}`}>
                        <Users className="w-3 h-3 mr-1" />Team {seats_taken}/{size}
                      </Badge>
                    )}
                  </div>
                  <div className="font-display font-bold text-lg mt-2">
                    {j.booking_type === "business"
                      ? `${j.sqft_bracket?.replaceAll("_", "-")} · ${j.space_type}`
                      : `${j.bedrooms} bed · ${j.bathrooms} bath`}
                    {j.deep_clean ? " · Deep" : ""}
                  </div>
                  <div className="text-xs text-slate-500 flex items-start gap-1 mt-1">
                    <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <div>
                      {j.street_address ? (
                        <>
                          <div className="text-slate-700">{j.street_address}{j.unit ? `, ${j.unit}` : ""}</div>
                          <div>{[j.city, j.state, j.postal_code].filter(Boolean).join(", ")}{j.country ? ` · ${j.country}` : ""}</div>
                        </>
                      ) : (<span>{j.postal_code}, {j.country}</span>)}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase text-slate-500">Scheduled</div>
                  <div className="text-sm flex items-center gap-1 mt-1"><Calendar className="w-3.5 h-3.5" />{new Date(j.scheduled_at).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-xs font-mono uppercase text-slate-500">Your take{size > 1 ? ` (÷${size})` : " (80%)"}</div>
                  <div className="font-display font-bold text-2xl text-teal-600 mt-1" data-testid={`per-person-${j.id}`}><DollarSign className="w-5 h-5 inline" />{per_person.toFixed(2)}</div>
                  {size > 1 && <div className="text-[10px] text-slate-400 font-mono">of ${j.cleaner_payout.toFixed(2)} total</div>}
                </div>
                <Button onClick={() => accept(j.id)} data-testid={`accept-btn-${j.id}`} className="bg-slate-900 hover:bg-slate-800 text-white h-11">
                  <Zap className="w-4 h-4 mr-1" />Accept · ${per_person.toFixed(0)}
                </Button>
              </div>
              {size > 1 && (
                <div className="mt-4 flex items-start gap-2 text-xs bg-blue-50 border border-blue-100 rounded-lg p-3 text-blue-900" data-testid={`split-preview-${j.id}`}>
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-blue-600" />
                  <div>
                    <div className="font-semibold">Team job — split preview</div>
                    <div className="mt-0.5">This is a {size}-cleaner assignment. On completion you receive <strong>${per_person.toFixed(2)}</strong> ({j.cleaner_payout.toFixed(2)} ÷ {size}). Each teammate gets the same share.</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
